/**
 * SCRUM-158 (P-8) — Zod schemas for the FloorRateOverride HTTP boundary.
 *
 * Endpoints covered (spec §10.1):
 *   #21 POST   /organizations/:slug/properties/:propertyId/floor-rate-overrides   ApplyFloorRateOverride
 *   #22 POST   /organizations/:slug/floor-rate-overrides/bulk                     BulkApplyFloorRateOverride
 *   #23 DELETE .../floor-rate-overrides/:overrideId                               RemoveFloorRateOverride (path-param only)
 *   #24 GET    /organizations/:slug/properties/:propertyId/floor-rate-overrides   ListFloorRateOverrides
 *
 * `FloorRateMode` domain values: 'simple' | 'smart'
 * (from floor-rate-mode.vo.ts — the schema mirrors the two-value domain type).
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/floor-rate-override.spec.ts
 */
import { z } from 'zod';
import { isoLocalDateString, instantString, centsSchema } from './_shared.js';
// ---------------------------------------------------------------------------
// ApplyFloorRateOverrideRequestSchema — endpoint #21
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/properties/:propertyId/floor-rate-overrides body.
 * Semi-open date range: dateFrom < dateTo (dateTo is exclusive).
 * `effectiveFloorCents`: nonnegative integer — the floor rate in cents.
 * `mode`: 'simple' | 'smart' (FloorRateMode from domain).
 */
export const ApplyFloorRateOverrideRequestSchema = z
    .object({
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    effectiveFloorCents: centsSchema,
    mode: z.enum(['simple', 'smart']),
    reason: z.string().min(1).max(500),
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
});
// ---------------------------------------------------------------------------
// BulkApplyFloorRateOverrideRequestSchema — endpoint #22
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/floor-rate-overrides/bulk body.
 * propertyIds: min 1, max 500 (mirrors BulkSetBaselineRequestSchema precedent).
 */
export const BulkApplyFloorRateOverrideRequestSchema = z
    .object({
    propertyIds: z.array(z.string().uuid()).min(1).max(500),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    effectiveFloorCents: centsSchema,
    mode: z.enum(['simple', 'smart']),
    reason: z.string().min(1).max(500),
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
});
// ---------------------------------------------------------------------------
// Response schemas — endpoints #21, #22, #24
// ---------------------------------------------------------------------------
/**
 * ApplyFloorRateOverrideResponseSchema — single-property apply response (endpoint #21).
 */
export const ApplyFloorRateOverrideResponseSchema = z.object({
    floorRateOverrideIds: z.array(z.string().uuid()),
    appliedDatesCount: z.number().int().nonnegative(),
    skippedBlockedDatesCount: z.number().int().nonnegative(),
});
/**
 * BulkApplyFloorRateOverrideEntrySchema — per-property result entry in the bulk response.
 */
export const BulkApplyFloorRateOverrideEntrySchema = z.object({
    propertyId: z.string().uuid(),
    outcome: z.enum(['applied', 'partial', 'skipped']),
    floorRateOverrideIds: z.array(z.string().uuid()),
    appliedDatesCount: z.number().int().nonnegative(),
    skippedBlockedDatesCount: z.number().int().nonnegative(),
    skippedConflictDatesCount: z.number().int().nonnegative(),
    reason: z
        .enum([
        'fully-blocked',
        'fully-conflict',
        'property-not-found',
        'conflict',
        // Declared since P-5 but never emitted by the floor UC; kept for wire
        // compatibility. SCRUM-248 (WH-6): the floor bulk UC emits
        // 'managed-by-provider' (spec §10.3 floor/base taxonomy) — the
        // price-adjustment bulk twin emits 'external-sync-enabled'.
        'external-sync-enabled',
        'managed-by-provider',
    ])
        .optional(),
});
/**
 * BulkApplyFloorRateOverrideResponseSchema — bulk apply response (endpoint #22).
 */
export const BulkApplyFloorRateOverrideResponseSchema = z.object({
    results: z.array(BulkApplyFloorRateOverrideEntrySchema),
});
/**
 * FloorRateOverrideItemSchema — one entry in the list response.
 */
export const FloorRateOverrideItemSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    effectiveFloorCents: centsSchema,
    mode: z.enum(['simple', 'smart']),
    reason: z.string(),
    createdAt: instantString,
});
/**
 * FloorRateOverrideListResponseSchema — GET .../floor-rate-overrides response (endpoint #24).
 */
export const FloorRateOverrideListResponseSchema = z.object({
    items: z.array(FloorRateOverrideItemSchema),
});
//# sourceMappingURL=floor-rate-override.js.map